import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { UserPlus, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useGuild } from '@/contexts/GuildContext';
import { API, authHeader, cardCls, timeAgo, PageShell, PageLoading, EmptyState, Avatar } from '@/lib/pageKit';

export default function Invites() {
  const { selectedGuild, selectedGuildId } = useGuild();
  const isGlobal = !!selectedGuild?.is_global;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!selectedGuildId || isGlobal) { setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/api/invites/${selectedGuildId}`, authHeader());
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load invite data');
    } finally {
      setLoading(false);
    }
  }, [selectedGuildId, isGlobal]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  if (loading) return <PageLoading />;
  const leaderboard = data?.leaderboard || [];
  const recent = data?.recent || [];

  return (
    <PageShell
      icon={UserPlus} title="Invites" subtitle="Who is bringing new members to your server"
      testId="invites-main" isGlobal={isGlobal} maxW="max-w-4xl"
      actions={
        <button onClick={load} className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors text-sm font-[Manrope] px-3 py-2 rounded-lg hover:bg-[var(--bg-elevated)] flex-shrink-0">
          <RefreshCw size={14} /> Refresh
        </button>
      }
    >
      {data && data.tracking_enabled === false && (
        <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 text-amber-400 text-sm font-[Manrope]">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <span>Invite tracking is off. The bot needs the <strong>Manage Server</strong> permission to see which invite each member used.</span>
        </div>
      )}

      <div>
        <h2 className="text-[var(--text-primary)] font-semibold font-[Outfit] text-lg mb-3">Leaderboard</h2>
        {leaderboard.length === 0 ? (
          <EmptyState icon={UserPlus} title="No invite data yet" hint="Joins will be tracked from now on" />
        ) : (
          <div className={`${cardCls} overflow-x-auto`}>
            <table className="w-full text-sm font-[Manrope]">
              <thead>
                <tr className="text-left text-[var(--text-muted)] text-xs uppercase tracking-widest border-b border-[var(--border)]">
                  <th className="px-4 py-3 font-medium w-10">#</th>
                  <th className="px-4 py-3 font-medium">Inviter</th>
                  <th className="px-4 py-3 font-medium text-right">Total</th>
                  <th className="px-4 py-3 font-medium text-right">Active</th>
                  <th className="px-4 py-3 font-medium text-right">Left</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {leaderboard.map((u, i) => (
                  <tr key={u.user_id} className="hover:bg-[var(--bg-hover)]">
                    <td className="px-4 py-3 text-[var(--text-muted)] font-mono">{i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar userId={u.user_id} avatar={u.avatar} name={u.username} />
                        <span className="text-[var(--text-primary)] truncate">{u.username || u.user_id}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-[var(--text-primary)] font-semibold">{u.total}</td>
                    <td className="px-4 py-3 text-right text-emerald-400">{u.active}</td>
                    <td className="px-4 py-3 text-right text-[var(--text-muted)]">{u.left}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h2 className="text-[var(--text-primary)] font-semibold font-[Outfit] text-lg mb-3">Recent joins</h2>
        {recent.length === 0 ? (
          <EmptyState icon={UserPlus} title="No recent joins" />
        ) : (
          <div className={`${cardCls} overflow-x-auto`}>
            <table className="w-full text-sm font-[Manrope]">
              <thead>
                <tr className="text-left text-[var(--text-muted)] text-xs uppercase tracking-widest border-b border-[var(--border)]">
                  <th className="px-4 py-3 font-medium">Member</th>
                  <th className="px-4 py-3 font-medium">Invited by</th>
                  <th className="px-4 py-3 font-medium">Code</th>
                  <th className="px-4 py-3 font-medium">Joined</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {recent.map((r, i) => (
                  <tr key={`${r.user_id}-${i}`} className="hover:bg-[var(--bg-hover)]">
                    <td className="px-4 py-3 text-[var(--text-primary)] whitespace-nowrap">{r.username || r.user_id}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)] whitespace-nowrap">{r.inviter_username || r.inviter_id || 'Unknown'}</td>
                    <td className="px-4 py-3 text-[var(--text-muted)] font-mono text-xs">{r.code || '—'}</td>
                    <td className="px-4 py-3 text-[var(--text-muted)] whitespace-nowrap">{timeAgo(r.joined_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${r.left ? 'bg-zinc-500/10 border-zinc-500/20 text-[var(--text-muted)]' : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'}`}>
                        {r.left ? 'Left' : 'Active'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PageShell>
  );
}
